<template>
  <div>
    <key-refresher
      ref="selectServersDialog"
      unique-key="select-servers-dialog"
    >
      <template v-slot="{ refreshKey }">
        <select-servers-dialog 
          :key="refreshKey"
          :value="showSelectServersDialog" 
          :date="date"
          :meal-period-id="mealPeriod.id"
          :location-id="location.id"
          :event-id="event.id"
          :for-event="forEvent"
          :servers="servers"
          @input="(open, server) => {
            showSelectServersDialog = open
            if (!open && $refs.selectServersDialog) $refs.selectServersDialog.refresh(200)
          }"
          @refresh="refreshMealPeriod"
          @refresh-meal-period="$emit('refresh-meal-period')"
        />
      </template>
    </key-refresher>
    <key-refresher
      ref="editServerDialog"
      unique-key="edit-server-dialog"
    >
      <template v-slot="{ refreshKey }">
        <edit-server-dialog 
          :key="refreshKey"
          v-bind="{date, eventId: event.id, forEvent, mealPeriod}"
          :show="showEditServerDialog"
          :server="selectedServer"
          @refresh-server="refreshServer"
          @input="open => {
            showEditServerDialog = open
            if (!open && $refs.editServerDialog) {
              $refs.editServerDialog.refresh(200)
              refreshMealPeriod()
            }
          }"
        />
      </template>
    </key-refresher>
    <key-refresher
      ref="addNewServerDialog"
      unique-key="add-server-dialog"
    >
      <template v-slot="{ refreshKey }">
        <edit-server-dialog
          :key="refreshKey"
          v-bind="{mealPeriod, date, eventId: event.id, forEvent}"
          :show="showAddNewServerDialog"
          new-server
          @input="(open, refreshAssignments) => {
            showAddNewServerDialog = open
            if (!open && refreshAssignments) refreshMealPeriod()
            if (!open && $refs.addNewServerDialog) {
              $refs.addNewServerDialog.refresh(200)
            }
          }"
        />
      </template>
    </key-refresher>
    <v-row 
      no-gutters
      class="px-4"
    >
      <v-col 
        cols="12"
        md="6"
      >
        Assigned Servers
      </v-col>
      <v-col 
        v-if="$vuetify.breakpoint.mdAndUp"
        cols="2"
      >
        Tables
      </v-col>
      <v-col 
        v-if="$vuetify.breakpoint.mdAndUp"
        cols="2"
      >
        Covers
      </v-col>
      <v-col 
        v-if="$vuetify.breakpoint.mdAndUp"
        cols="2"
      >
        Reservations
      </v-col>
    </v-row>
    <div 
      v-if="!assignedServers.length"
      class="font-weight-bold text-center pa-2"
    >
      No Servers Assigned
    </div>
    <div v-else>
      <template v-for="server in assignedServers">
        <scaler 
          :key="'assigned-server-' + server.serverId"
          :scaled="serverExpanded !== -1 && serverExpanded !== server._index"
          x-axis="0.95"
          y-axis="0.95"
        >
          <server-card 
            :ref="`server-card-${server.serverId}`"
            v-bind="{date, tableConfigId, serverAssignmentsMap, eventId: event.id, forEvent}"
            :reservation-counts="assignmentsMap[server.serverId]"
            :disabled="serverExpanded !== -1 && serverExpanded !== server._index"
            :server="allServersMap[server.serverId]"
            :expanded="server.expanded"
            :meal-period-id="mealPeriod.id"
            :location-id="location.id"
            class="ma-2"
            @click:server="openEditServerDialog(allServersMap[server.serverId])"
            @click:table="expandServer(server)"
            @set-tables="$emit('set-tables', $event.map(t => allTablesMap[t.locationTableId]))"
            @saved="refreshMealPeriod"
          />
        </scaler>
      </template>
    </div>
    <v-divider class="my-4" />
    <v-row 
      no-gutters
      class="px-4"
    >
      <v-col 
        cols="12"
        md="6"
      >
        Unassigned Servers
      </v-col>
      <v-col 
        v-if="$vuetify.breakpoint.mdAndUp"
        cols="2"
      >
        Tables
      </v-col>
      <v-col 
        v-if="$vuetify.breakpoint.mdAndUp"
        cols="2"
      >
        Covers
      </v-col>
      <v-col 
        v-if="$vuetify.breakpoint.mdAndUp"
        cols="2"
      >
        Reservations
      </v-col>
    </v-row>
    <div 
      v-if="!servers.length"
      class="font-weight-bold text-center pa-2"
    >
      No Servers Selected
    </div>
    <div v-else>
      <template v-for="server in unassignedServers">
        <scaler
          :key="'unassigned-server-' + server.serverId"
          :scaled="serverExpanded !== -1 && serverExpanded !== server._index"
          y-axis="0.95"
          x-axis="0.95"
        >
          <server-card 
            :ref="`server-card-${server.serverId}`"
            v-bind="{date, tableConfigId, serverAssignmentsMap, eventId: event.id, forEvent}"
            :disabled="serverExpanded !== -1 && serverExpanded !== server._index"
            :server="allServersMap[server.serverId]"
            :expanded="server.expanded"
            :meal-period-id="mealPeriod.id"
            :location-id="location.id"
            class="ma-2"
            @click:server="openEditServerDialog(allServersMap[server.serverId])"
            @click:table="expandServer(server)"
            @set-tables="$emit('set-tables', $event.map(t => allTablesMap[t.locationTableId]))"
            @saved="refreshMealPeriod"
          />
        </scaler>
      </template>
    </div>
    <v-row no-gutters>
      <v-col
        cols="12"
        md="12"
        xl="6"
        order="2"
        order-xl="1"
        :class="[
          $vuetify.breakpoint.lgAndDown ? 'px-2' : 'pl-2 pr-1',
          'pb-1',
          'pt-3'
        ]"
      >
        <v-btn
          block
          :color="$ftStyle.pageBarSaveDirtyButtonColor"
          light
          @click="showAddNewServerDialog = true"
        >
          {{ $vuetify.breakpoint.mdAndUp ? '+ Create New Server' : '+ Create Server' }}  
        </v-btn>
      </v-col>
      <v-col
        cols="12"
        md="12"
        xl="6"
        order="1"
        order-xl="2"
        :class="[
          $vuetify.breakpoint.lgAndDown ? 'px-2' : 'pr-2 pl-1',
          'pb-1',
          'pt-3'
        ]"
      >
        <v-btn
          block
          color="primary"
          dark
          @click="showSelectServersDialog = true"
        >
          {{ $vuetify.breakpoint.mdAndUp ? `+ Add Servers to ${forEvent ? 'Event' : 'Meal Period'}` : '+ Add Servers' }}  
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ServerCard from './ServerCard'
import SelectServersDialog from './SelectServersDialog'
import EditServerDialog from '../../helperComponents/EditServerDialog'
import errorHandler from '@/ft-vue-core/mixins/errorHandler'
import KeyRefresher from '@/ft-vue-core/components/helpers/KeyRefresher'
import Scaler from '@/ft-vue-core/components/transformers/Scaler'
export default {
  name: 'ServersTab',
  components: {ServerCard, SelectServersDialog, EditServerDialog, KeyRefresher, Scaler},
  mixins: [errorHandler],
  props: {
    location: {
      type: Object,
      default () {return {}}
    },
    mealPeriod: {
      type: Object,
      default () {return {}}
    },
    serverAssignmentsMap: {
      type: Object,
      default () {return {}}
    },
    forEvent: Boolean,
    event: {
      type: Object,
      default () {return {}}
    },
    tableConfigId: Number,
    date: [String, Number],
  },
  data () {
    return {
      servers: [],
      assignmentsMap: {},
      allServersMap: {},
      allTablesMap: {},
      selectedServer: undefined,
      showSelectServersDialog: false,
      showEditServerDialog: false,
      showAddNewServerDialog: false,
    }
  },
  computed: {
    /**
     * Map the contents of `servers` by `id` while logging the local index
     * of each entry
     * @see servers
     */
    serversMap () {
      return this.servers.reduce((acc, r, i) => {
        r._index = i
        acc[r.id] = r
        return acc
      }, {}) ?? {}
    },
    selectedServersMap () {
      return this.mealPeriod.servers?.reduce((acc, r) => {
        acc[r.serverId] = r
        return acc
      }, {}) ?? {}
    },
    unassignedServers () {
      if (this.forEvent) {
        return this.servers.reduce((acc, s) => {
          if (s.assignments?.every(a => a.locationId !== this.location.id) ?? true) acc.push(s)
          return acc
        }, []) ?? []
      }
      return this.servers.filter(s => !s.assigned)
    },
    assignedServers () {
      if (this.forEvent) {
        return this.servers.reduce((acc, s) => {
          if (s.assignments?.some(a => a.locationId === this.location.id)) acc.push(s)
          return acc
        }, []) ?? []
      }
      return this.servers.filter(s => s.assigned)
    },
    /**
     * Check if there exists any expanded server in `servers`
     * @see servers
     */
    serverExpanded () {
      return this.servers.findIndex(s => s.expanded)
    },
    /** Tables assigned to the selected server as _DiningLocationTable_ objects */
    selectedServerTables: {
      get () {
        return this.selectedServer?.tables.map(t => this.allTablesMap[t.locationTableId])
      },
      set (tables) {
        this.$set(this.selectedServer, 'tables', tables)
      }
    },
    /** `selectedServerTables` mapped by `id` with local index */
    selectedServerTablesMap () {
      return this.selectedServerTables.reduce((acc, t, i) => {
        t._index = i
        acc[t.id] = t
        return acc
      }, {})
    },
    /**
     * API Command Object formatted to get map of server assignments in non-events
     */
    mealPeriodAssignmentsCommand () {
      return {
        command: 'diningGetServerAssignments',
        load: r => this.assignmentsMap = r[0]?.result ?? {}, // eslint-disable-line
        params: {
          filter_methods: this.forEvent
            ? {filterEventData: [this.event.id, this.location.id]}
            : {filterData: [this.mealPeriod.id, this.location.id, this.date]}
        }
      }
    },
    /**
     * API Command Object formatted to get -ALL- relevant tables for location
     */
    allTablesCommand () {
      return {
        command: 'diningLocationTable',
        load: r => {
          this.allTablesMap = r.reduce((acc, t) => { // eslint-disable-line
            acc[t.id] = t
            return acc
          }, {}) ?? {}
        },
      }
    },
    /**
     * API Command Object formatted to get -ALL- relevant servers for Meal Period/Event
     */
    allServersCommand () {
      const {forEvent} = this.$props
      return {
        command: 'diningServer',
        load: r => {                                   // Assignment allowed
          this.allServersMap = r?.reduce((acc, s) => { // eslint-disable-line 
            acc[s.id] = s
            return acc
          }, {}) ?? {}
        },
        params: {
          children: true,
          filter: forEvent
            ? [
              {field: 'diningServerEventTables.eventId', operator: 'EQUAL', values: [this.event.id]},
              {field: 'diningServerEventTables.locationId', operator: 'EQUAL', values: [this.location.id]}
            ]
            : [{field: 'diningServerTables.mealPeriodId', operator: 'EQUAL', values: [this.mealPeriod.id]}],
          filter_methods: forEvent
            ? {filterByInLocation: [this.location.id]}
            : {filterByInMealPeriod: [this.mealPeriod.id], filterTablesByDate: [this.date]},
          include_fields: forEvent
            ? ['eventTables']
            : undefined
        }
      }
    },
    /**
     * API Command Object formatted to get relevant servers for Meal Period/Event
     */
    assigmentServersCommand () {
      const {forEvent} = this.$props
      return {
        command: forEvent ? 'diningEvent' : 'diningMealPeriod',
        load: r => this.servers = r[0]?.servers ?? [], // eslint-disable-line
        params: {
          children: true,
          filter: forEvent
            ? [{field: 'id', operator: 'EQUAL', values: [this.event.id]}]
            : [{field: 'id', operator: 'EQUAL', values: [this.mealPeriod.id]}],
          filter_methods: forEvent 
            ? {}
            : {filterServersByDate: [this.date]},
          include_fields: forEvent
            ? ['servers', 'diningServer.eventTables']
            : undefined
        }
      }

    }
  },
  watch: {
    date () {this.refreshMealPeriod()},
    'mealPeriod.id' () {this.refreshMealPeriod()},
    'location.id' () {this.refreshMealPeriod()},
    showEditServerDialog (newVal) {
      if (!newVal) {
        this.selectedServer = undefined
      }
    },
    serverExpanded (value) {
      let selectedServer
      if (value !== -1) selectedServer = this.allServersMap[this.servers[value].serverId]
      this.selectedServer = selectedServer
      this.$emit('select-server', selectedServer)
    }
  },
  mounted () {this.refreshMealPeriod()},
  methods: {
    /**
     * Referenced from parent using $refs
     * @param {Object} table DiningLocationTable object representing selected table from canvas
     */
    selectTable (table) {
      if (!this.selectedServer) return
      const [serverRef] = this.$refs[`server-card-${this.selectedServer.id}`] ?? []
      serverRef?.selectTable(table)
    },
    setTables (table, add) {
      if (add) {
        this.$emit('set-tables', this.selectedServerTables)
      } else {
        const index = this.selectedServerTablesMap[table.id]?._index ?? -1
        if (index !== -1) {
          this.selectedServerTables.splice(index, 1)
          this.$emit('set-tables', this.selectedServerTables)
        }
      }
    },  
    refreshMealPeriod () {
      const commands = [
        this.allServersCommand,
        this.assigmentServersCommand,
        this.allTablesCommand,
        this.mealPeriodAssignmentsCommand
      ]
      // if (!this.forEvent) commands.push(this.mealPeriodAssignmentsCommand)
      this.$ftAPI().get(this.addResourceErrorHandler(commands))
      this.$emit('refresh-server-assignments')
    },
    expandServer (server) {
      const {expanded} = this.serversMap[server.id] ?? {}
      this.$set(this.servers[this.serversMap[server.id]._index], 'expanded', !expanded)
    },
    refreshServer (server) {
      const updateIndex = this.servers.findIndex(s => s.id === server.id)
      if (updateIndex !== -1) this.servers.splice(updateIndex, 1, server)
    },
    openEditServerDialog (server) {
      this.selectedServer = server
      this.showEditServerDialog = true
    },
  }
}
</script>
